import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, Eye, EyeOff, Search, Download, Share2, Loader2 } from "lucide-react";
import { toPng } from "html-to-image";
import api from "../lib/api";
import { Reveal, useSEO } from "../components/site/Reveal";
import { toDataUrl } from "../lib/images";
import Newsletter from "../components/site/Newsletter";
import FullImage from "../components/site/FullImage";

const QuestionCard = ({ q, index, onCard, busy }) => {
  const [revealed, setRevealed] = React.useState(false);
  const canReveal = q.answer && q.answer_visible;
  return (
    <Reveal delay={(index % 4) * 0.05}>
      <div
        className="h-full flex flex-col border border-cream/12 rounded-2xl bg-night-surface p-7"
        data-testid={`daily-question-card-${q.id}`}
      >
        <div className="flex items-center justify-between mb-4">
          <HelpCircle className="w-6 h-6 text-gold/50" />
          {q.theme && (
            <span className="text-[10px] uppercase tracking-widest border border-cream/25 rounded-full px-2.5 py-0.5 text-cream/55">
              {q.theme}
            </span>
          )}
        </div>
        {q.image && (
          <FullImage src={q.image} alt="" className="h-64 rounded-xl mb-5" testId={`daily-question-image-${q.id}`} />
        )}
        <p className="font-serif text-xl text-gold leading-snug mb-5">{q.question}</p>

        {canReveal && (
          <div className="pt-2">
            {!revealed ? (
              <button
                onClick={() => setRevealed(true)}
                data-testid={`daily-question-reveal-${q.id}`}
                className="inline-flex items-center gap-2 bg-gold text-night text-sm font-semibold px-4 py-2 rounded-full hover:bg-gold-light transition-colors"
              >
                <Eye className="w-4 h-4" /> Reveal the meaning
              </button>
            ) : (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ duration: 0.35 }}
                  className="rounded-xl bg-forest/25 border border-gold/20 p-5"
                  data-testid={`daily-question-answer-${q.id}`}
                >
                  <p className="uppercase tracking-[0.2em] text-[11px] text-gold-soft mb-2">Ìtumọ̀ · Meaning</p>
                  <p className="text-cream/80 text-[15px] leading-relaxed">{q.answer}</p>
                  <button
                    onClick={() => setRevealed(false)}
                    data-testid={`daily-question-hide-${q.id}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm text-gold hover:text-gold-light transition-colors"
                  >
                    <EyeOff className="w-4 h-4" /> Hide
                  </button>
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        )}

        {/* Downloadable branded images */}
        <div className="flex flex-wrap gap-2 mt-auto pt-5">
          <button
            onClick={() => onCard(q, "download", "question")}
            disabled={busy !== ""}
            data-testid={`daily-question-download-${q.id}`}
            className="inline-flex items-center gap-2 bg-gold text-night text-sm font-semibold px-4 py-2 rounded-full hover:bg-gold-light transition-colors disabled:opacity-50"
          >
            {busy === `download-question-${q.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Question Card
          </button>
          {canReveal && (
            <button
              onClick={() => onCard(q, "download", "answer")}
              disabled={busy !== ""}
              data-testid={`daily-question-answer-card-${q.id}`}
              className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors disabled:opacity-50"
            >
              {busy === `download-answer-${q.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Answer Card
            </button>
          )}
          <button
            onClick={() => onCard(q, "share", "question")}
            disabled={busy !== ""}
            data-testid={`daily-question-share-${q.id}`}
            className="inline-flex items-center gap-2 border border-cream/25 text-cream text-sm font-semibold px-4 py-2 rounded-full hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
          >
            {busy === `share-question-${q.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            Share
          </button>
        </div>
      </div>
    </Reveal>
  );
};

export default function DailyQuestion() {
  useSEO(
    "Question of the Day — Yorùbá Daily Reflection (Ìbéèrè t'òní)",
    "A growing collection of Yorùbá daily reflection questions and riddles. Ponder each question, then reveal its deeper meaning and wisdom."
  );

  const [questions, setQuestions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [term, setTerm] = React.useState("");
  const [shareTarget, setShareTarget] = React.useState(null);
  const [shareVariant, setShareVariant] = React.useState("question");
  const [cardImg, setCardImg] = React.useState(null);
  const [busy, setBusy] = React.useState("");
  const cardRef = React.useRef(null);

  React.useEffect(() => {
    api
      .get("/daily-questions")
      .then((r) => setQuestions(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Renders a branded card (question-only or question+answer) to PNG, then downloads or shares it.
  const onCard = async (q, action, variant = "question") => {
    setShareTarget(q);
    setShareVariant(variant);
    setBusy(`${action}-${variant}-${q.id}`);
    setCardImg(q.image ? await toDataUrl(q.image) : null);
    await new Promise((res) => setTimeout(res, 120));
    try {
      if (!cardRef.current) return;
      const url = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true, backgroundColor: "#12100e" });
      if (!url) return;
      const filename = variant === "answer" ? "yoruba-question-answer.png" : "yoruba-question.png";
      if (action === "share") {
        const blob = await (await fetch(url)).blob();
        const file = new File([blob], filename, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "Question of the Day", text: `${q.question} — via ÀsÀNigHT Worldwide` });
          return;
        }
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
    } catch (e) {
      /* ignore (user cancelled, etc.) */
    } finally {
      setBusy("");
    }
  };

  const fold = (s) => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const filtered = questions.filter((q) => {
    if (!term.trim()) return true;
    const t = fold(term.trim());
    return fold(q.question).includes(t) || fold(q.answer).includes(t) || fold(q.theme).includes(t);
  });

  return (
    <div className="bg-night text-cream min-h-screen">
      {/* HERO */}
      <header className="max-w-5xl mx-auto px-6 pt-24 pb-10">
        <Reveal>
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="w-4 h-4 text-gold" />
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft">Question of the Day · Ìbéèrè t'òní</p>
          </div>
          <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] mb-5" data-testid="daily-question-title">
            Daily Reflection
          </h1>
          <p className="text-cream/70 text-lg leading-relaxed max-w-2xl">
            A new Yorùbá question each day — a proverb, a riddle, a thought to ponder. Sit with it,
            then reveal the deeper meaning woven into our heritage.
          </p>
        </Reveal>
      </header>

      {/* SEARCH */}
      <div className="max-w-5xl mx-auto px-6 pb-8">
        <Reveal>
          <div className="relative">
            <Search className="w-4 h-4 text-cream/40 absolute left-5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search questions or meanings…"
              data-testid="daily-question-search-input"
              className="w-full rounded-full bg-night-surface border border-cream/20 pl-12 pr-6 py-3.5 text-cream placeholder:text-cream/40 focus:outline-none focus:border-gold transition-colors"
            />
          </div>
        </Reveal>
      </div>

      {/* LIST */}
      <div className="max-w-5xl mx-auto px-6 pb-24">
        {loading ? (
          <p className="text-cream/50 py-16 text-center">Loading questions…</p>
        ) : filtered.length === 0 ? (
          <p className="text-cream/50 py-16 text-center" data-testid="daily-question-empty">
            No questions yet — check back soon for today's reflection.
          </p>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            {filtered.map((q, i) => (
              <QuestionCard key={q.id} q={q} index={i} onCard={onCard} busy={busy} />
            ))}
          </div>
        )}
      </div>

      {/* Hidden branded share card — QUESTION ONLY (no answer/meaning) */}
      {shareTarget && (
        <div aria-hidden="true" style={{ position: "fixed", top: 0, left: "-20000px", pointerEvents: "none" }}>
          <div
            ref={cardRef}
            data-testid="daily-question-share-card"
            style={{
              width: 1080,
              height: 1080,
              background: "linear-gradient(155deg, #1b3327 0%, #12100e 55%)",
              color: "#F5EFE6",
              padding: 90,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              boxSizing: "border-box",
              position: "relative",
            }}
          >
            <div style={{ position: "absolute", inset: 40, border: "2px solid rgba(212,169,74,0.35)", borderRadius: 28, pointerEvents: "none" }} />

            <div style={{ position: "relative" }}>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 46, color: "#D4A94A", lineHeight: 1 }}>
                ÀsÀNigHT
              </div>
              <div style={{ fontSize: 16, letterSpacing: 8, color: "rgba(245,239,230,0.6)", marginTop: 6 }}>
                WORLDWIDE
              </div>
              <div style={{ fontSize: 20, letterSpacing: 4, textTransform: "uppercase", color: "#E8D5A8", marginTop: 40 }}>
                Question of the Day · Ìbéèrè t'òní
              </div>
            </div>

            <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
              {cardImg ? (
                <img
                  src={cardImg}
                  alt=""
                  style={{ width: "100%", height: shareVariant === "answer" ? 300 : 420, objectFit: "cover", borderRadius: 20, border: "3px solid rgba(212,169,74,0.55)", marginBottom: 30, display: "block" }}
                />
              ) : (
                <div style={{ fontSize: 120, color: "rgba(212,169,74,0.35)", fontFamily: "Georgia, serif", lineHeight: 0.6, marginBottom: 10 }}>?</div>
              )}
              <div
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  color: "#D4A94A",
                  fontSize:
                    shareTarget.question.length > 130 ? (shareVariant === "answer" ? 34 : 40) : shareTarget.question.length > 80 ? (shareVariant === "answer" ? 40 : 48) : shareVariant === "answer" ? 46 : 56,
                  lineHeight: 1.16,
                }}
              >
                {shareTarget.question}
              </div>
              {shareVariant === "answer" ? (
                <div style={{ marginTop: 26 }}>
                  <div style={{ fontSize: 20, letterSpacing: 4, textTransform: "uppercase", color: "#E8D5A8", marginBottom: 12 }}>
                    Ìtumọ̀ · Meaning
                  </div>
                  <div style={{ fontSize: shareTarget.answer && shareTarget.answer.length > 280 ? 26 : 32, lineHeight: 1.4, color: "#F5EFE6" }}>
                    {shareTarget.answer}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 26, lineHeight: 1.4, color: "rgba(245,239,230,0.75)", marginTop: 24, fontStyle: "italic" }}>
                  {shareTarget.card_caption || "Kíló túnmọ̀ sí? · What does it mean? Reflect, then discover the answer."}
                </div>
              )}
            </div>

            <div style={{ position: "relative", borderTop: "1px solid rgba(245,239,230,0.15)", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 26, color: "#D4A94A", letterSpacing: 1 }}>asanightworldwide.com</div>
              {shareTarget.theme && (
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontSize: 24, color: "rgba(245,239,230,0.5)" }}>
                  {shareTarget.theme}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Newsletter />
    </div>
  );
}
